import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Building2, Loader2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#1F3C88] noise-bg overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1756435292384-1bf32eff7baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Luxury home"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="relative z-10 flex flex-col justify-between p-14 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-sm bg-[#F7941D] flex items-center justify-center">
              <Building2 className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <span className="font-serif text-2xl">MattC Team</span>
          </div>
          <div>
            <h1 className="font-serif text-5xl xl:text-6xl leading-tight tracking-tight">
              One command center for your entire brokerage.
            </h1>
            <p className="mt-6 text-[#DBE2F5] text-lg max-w-md">
              Sales, transactions, listings and search — unified into a single, elegant workspace tailored to every role on your team.
            </p>
          </div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#A9B6D9]">Residential Real Estate Operations</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#FDFDFB]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-sm bg-[#F7941D] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-serif text-2xl text-[#1F3C88]">MattC Team</span>
          </div>
          <h2 className="font-serif text-4xl tracking-tight text-[#0A0A0A]">Welcome back</h2>
          <p className="mt-2 text-[#575755]">Sign in to access your workspace.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#575755]">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full px-4 py-3 bg-white border border-[#E5E5E2] rounded-sm focus:ring-2 focus:ring-[#F7941D] focus:border-transparent focus:outline-none transition-colors duration-200"
                placeholder="you@mattcteam.com"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#575755]">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full px-4 py-3 bg-white border border-[#E5E5E2] rounded-sm focus:ring-2 focus:ring-[#F7941D] focus:border-transparent focus:outline-none transition-colors duration-200"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-[#992B2B] bg-[#F6E4E4] px-4 py-3 rounded-sm">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#F7941D] text-white font-semibold hover:bg-[#E08312] transition-colors duration-200 disabled:opacity-60"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
